import React from "react";
import Typograpy from "../../../../components/ui/Typography";
import Button from "../../../../components/ui/Button";
import { Copy, Send, Trash, User2Icon } from "lucide-react";
import { styles } from "./styles";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import { remittanceApi } from "../../api/remittanceApi";
import { AppStrings, copyToClipboard, logApiErrorToSentry } from "../../utils";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ConfirmCancelModal, KycAggrementModal, KycSuccessModal } from "../../components/modal";
import MixpanelConfig from "../../../../constants/MixpanelConfig";

const RemitterDetails = ({ remitterData }: { remitterData: any }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { remitter } = useAppSelector((state) => state.remittance);

  const resendRemitterKycLink = async (remitterId: string, type: string) => {
    try {
      dispatch(remittanceActions.setResendRemitterKycLink({ loading: true, loadingType: type, error: null }));
      const response = await remittanceApi.getRemitterResendKycLink(remitterId);
      const link = response?.data?.link;
      const error = response?.data?.error;
      if (link) {
        dispatch(remittanceActions.setResendRemitterKycLink({ loading: false, loadingType: "", error: null }));
        type === "send" ? toast.success(AppStrings.REMITTER_DETAILS.KYC_LINK_SENT) : copyClipboardHandler(link);
        return;
      }
      dispatch(remittanceActions.setResendRemitterKycLink({ loading: false, loadingType: "", error }));
      toast.error(error);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setResendRemitterKycLink({ loading: false, loadingType: "", error: errorMsg }));
      logApiErrorToSentry(err, "resendRemitterKycLink");
    }
  };

  const deleteRemitterByUserKey = async (remitterId: string) => {
    try {
      dispatch(remittanceActions.setDeleteRemitter({ loading: true, error: null }));
      const response = await remittanceApi.getDeleteRemitter(remitterId);
      dispatch(remittanceActions.setModalType(""));
      dispatch(remittanceActions.setDeleteRemitter({ loading: false, error: null }));
      dispatch(remittanceActions.setRemitterListState({ reload: true }));
      let successMessage = response?.data?.message || response?.data;
      toast.success(successMessage == "Vendor User Account deactivated successfully" ? "Remitter has been deleted successfully" : successMessage);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setDeleteRemitter({ loading: false, error: errorMsg }));
      logApiErrorToSentry(err, "deleteRemitterByUserKey");
    }
  };
  
  const copyClipboardHandler = (link: string) => {
    copyToClipboard(
      link,
      AppStrings.REMITTER_DETAILS.KYC_LINK_COPIED,
      AppStrings.REMITTER_DETAILS.KYC_LINK_NOT_COPIED
    );
  }

  const getCkycStatus = async () => {
    try {
      dispatch(remittanceActions.setRemitterDocumentsSection({ ckycStatus: { loading: true, error: null } }));
      const response = await remittanceApi.getRemitterCkycStatus(remitterData?.id);
      if(response?.data) {
        getRemitterDocuments();
      }
      dispatch(remittanceActions.setRemitterDocumentsSection({ ckycStatus: { loading: false, error: null, data: response?.data } }));
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setRemitterDocumentsSection({ ckycStatus: { loading: false, error: errorMsg } }));
      logApiErrorToSentry(err, "getCkycStatus remittanceApi.getRemitterCkycStatus");
    }
  }

  const getRemitterDocuments = async () => {
    try {
      dispatch(remittanceActions.setRemitterDocumentsSection({ userDocuments: { loading: true, error: null, data: [] } }));
      const response = await remittanceApi.getRemitterDocuments(remitterData?.userKey);
      const hasPanCard = response?.data?.userDocs?.some(
        (doc: any) => doc.docName?.includes("PAN_CARD")
      ) ?? false;
      dispatch(remittanceActions.setModalType(hasPanCard ? "USER_DOCUMENTS" : "KYC_AGGREMENT"));
      dispatch(remittanceActions.setRemitterDocumentsSection({ userDocuments: { loading: false, error: null, data: response?.data?.userDocs } }));
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error?.type || err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      toast.error(errorMsg);
      dispatch(remittanceActions.setRemitterDocumentsSection({ userDocuments: { loading: false, error: errorMsg } }));
      logApiErrorToSentry(err, "getRemitterDocuments remittanceApi.getRemitterDocuments");
    }
  }

  const onSelectRemitter = () => {
    dispatch(remittanceActions.resetRemitterDocumentsState());
    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Remitter_Select, {
      authState: authState,
      remitterState: remitterState,
      selectedRemitter: remitterData
    });
    if (!remitterData?.isDocVerified) {
      dispatch(remittanceActions.setSelectedRemitter(remitterData));
      getCkycStatus();
    } else if (
      remitterData?.gender === null ||
      remitterData?.nationality === null ||
      remitterData?.gender === "" ||
      remitterData?.nationality === "" ||
      remitterData?.employmentStatus === null ||
      remitterData?.industryType === null ||
      remitterData?.employmentStatus === "" ||
      remitterData?.industryType === ""
    ) {
      dispatch(remittanceActions.setSelectedRemitter(remitterData));
      navigate("/send/remitter/view");
    } else {
      dispatch(remittanceActions.setSelectedRemitter(remitterData));
      dispatch(remittanceActions.setActiveStepNumber(3));
      dispatch(remittanceActions.resetRecipientState());
      navigate("/send/recipient");
    }
  }

  const selectRemitterLoading = remitter?.remitterDocuments?.ckycStatus?.loading || remitter?.remitterDocuments?.userDocuments?.loading;

  return (
    <div className={styles.remitterDetails.container}>
      <div className={styles.remitterDetails.grid}>

        {/* LEFT SECTION */}
        <div className={styles.remitterDetails.leftCol}>

          <div className={styles.remitterDetails.row}>
            <Typograpy tagType="div" text="Date of Birth: " className="text-sm font-normal text-black" />
            <Typograpy tagType="div" text={remitterData?.dob} className="text-sm font-normal text-black" />
          </div>

          <div className={styles.remitterDetails.row}>
            <Typograpy tagType="div" text="City: " className="text-sm font-normal text-black" />
            <Typograpy tagType="div" text={remitterData?.city} className="text-sm font-normal text-black" />
          </div>

          <div className={styles.remitterDetails.rowWide}>
            <Typograpy tagType="div" text="Address: " className="text-sm font-normal text-black" />
            <Typograpy
              tagType="div"
              text={[
                remitterData?.addressLine1?.trim(),
                remitterData?.addressLine2?.trim(),
              ].filter(Boolean).join(", ")}
              className="text-sm font-normal text-black text-wrap"
            />
          </div>

        </div>

        {/* RIGHT SECTION */}
        <div className={styles.remitterDetails.rightCol}>

          {!remitterData?.isDocVerified && (
            <div className={styles.remitterDetails.kycBox}>
              <Typograpy
                tagType="div"
                text="Share link to complete KYC"
                className="text-xs font-normal text-black-100"
              />

              <div className="flex justify-between mt-1 gap-2">
                <Button
                  bgColor="bg-[#fff]"
                  fontStyle={{
                    fontFamily: "font-Inter",
                    fontSize: "text-xs",
                    fontWeight: "font-normal",
                    fontColor: "text-black",
                  }}
                  className={styles.remitterDetails.buttonBase}
                  onClickHandler={() => resendRemitterKycLink(remitterData?.userKey, "send")}
                  isLoading={remitter?.resendRemitterKycLink?.loading && remitter?.resendRemitterKycLink?.loadingType === "send"}
                  disabled={remitter?.resendRemitterKycLink?.loading && remitter?.resendRemitterKycLink?.loadingType === "send"}
                >
                  <div className={styles.remitterDetails.sendCopyButtonText}>
                    <Send width={10} height={10} />
                    {"Send"}
                  </div>
                </Button>

                <Button
                  bgColor="bg-[#fff]"
                  fontStyle={{
                    fontFamily: "font-Inter",
                    fontSize: "text-xs",
                    fontWeight: "font-normal",
                    fontColor: "text-black",
                  }}
                  className={styles.remitterDetails.buttonBase}
                  onClickHandler={() => resendRemitterKycLink(remitterData?.userKey, "copy")}
                  isLoading={remitter?.resendRemitterKycLink?.loading && remitter?.resendRemitterKycLink?.loadingType === "copy"}
                  disabled={remitter?.resendRemitterKycLink?.loading && remitter?.resendRemitterKycLink?.loadingType === "copy"}
                >
                  <div className={styles.remitterDetails.sendCopyButtonText}>
                    <Copy width={10} height={10} />
                    {"Copy"}
                  </div>
                </Button>
              </div>
            </div>
          )}

          <div>
            <Button
              bgColor="bg-[#006EFF]"
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-normal",
                fontColor: "text-white",
              }}
              className={styles.remitterDetails.primaryBtn}
              onClickHandler={onSelectRemitter}
              disabled={selectRemitterLoading}
              isLoading={selectRemitterLoading}
            >
              <div className="flex items-center gap-1">
                <User2Icon width={15} height={15} />
                {"Select Remitter"}
              </div>
            </Button>
            <div className="mt-1" />
            <Button
              bgColor="bg-blue-50"
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-normal",
                fontColor: "text-red-500",
              }}
              className={styles.remitterDetails.deleteBtn}
              onClickHandler={() => dispatch(remittanceActions.setModalType("DELETE_REMITTER"))}
            >
              <div className={styles.remitterDetails.deleteBtnText}>
                <Trash width={12} height={12} className="text-red-500" />
                {"Delete Remitter"}
              </div>
            </Button>
          </div>

        </div>

      </div>
      <ConfirmCancelModal
        visible={remitterState?.modalType === "DELETE_REMITTER"}
        title={AppStrings.DELETE_REMITTER_MODAL.TITLE}
        description={AppStrings.DELETE_REMITTER_MODAL.DESCRIPTION}
        onConfirm={() => {
          deleteRemitterByUserKey(remitterData?.userKey);
        }}
        onClose={() => {
          dispatch(remittanceActions.setModalType(""));
        }}
        loading={remitter?.deleteRemitter?.loading}
        disabled={remitter?.deleteRemitter?.loading}
      />
    </div>
  )
};

export default RemitterDetails;
